import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { KeyInventoryItem, KeyTransaction } from '../types';
import { inventoryApi, transactionsApi } from '../services/api';

interface KeyInventoryState {
  inventory: KeyInventoryItem[];
  transactions: KeyTransaction[];
  loading: boolean;
  error: string | null;
}

const initialState: KeyInventoryState = {
  inventory: [],
  transactions: [],
  loading: false,
  error: null,
};

// Async thunks for inventory
export const fetchInventory = createAsyncThunk(
  'keyInventory/fetchInventory',
  async (_, { rejectWithValue }) => {
    try {
      return await inventoryApi.getAll();
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const createInventoryItemAsync = createAsyncThunk(
  'keyInventory/createInventoryItem',
  async (item: KeyInventoryItem, { rejectWithValue }) => {
    try {
      return await inventoryApi.create(item);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const updateInventoryItemAsync = createAsyncThunk(
  'keyInventory/updateInventoryItem',
  async (item: KeyInventoryItem, { rejectWithValue }) => {
    try {
      return await inventoryApi.update(item);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const deleteInventoryItemAsync = createAsyncThunk(
  'keyInventory/deleteInventoryItem',
  async (id: string, { rejectWithValue }) => {
    try {
      await inventoryApi.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// Async thunks for transactions
export const fetchTransactions = createAsyncThunk(
  'keyInventory/fetchTransactions',
  async (_, { rejectWithValue }) => {
    try {
      return await transactionsApi.getAll();
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const createTransactionAsync = createAsyncThunk(
  'keyInventory/createTransaction',
  async (transaction: KeyTransaction, { rejectWithValue }) => {
    try {
      return await transactionsApi.create(transaction);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

const keyInventorySlice = createSlice({
  name: 'keyInventory',
  initialState,
  reducers: {
    setInventory: (state, action: PayloadAction<KeyInventoryItem[]>) => {
      state.inventory = action.payload;
    },
    addInventoryItem: (state, action: PayloadAction<KeyInventoryItem>) => {
      state.inventory.push(action.payload);
    },
    updateInventoryItem: (state, action: PayloadAction<KeyInventoryItem>) => {
      const index = state.inventory.findIndex(item => item.id === action.payload.id);
      if (index !== -1) {
        state.inventory[index] = action.payload;
      }
    },
    removeInventoryItem: (state, action: PayloadAction<string>) => {
      state.inventory = state.inventory.filter(item => item.id !== action.payload);
    },
    setTransactions: (state, action: PayloadAction<KeyTransaction[]>) => {
      state.transactions = action.payload;
    },
    addTransaction: (state, action: PayloadAction<KeyTransaction>) => {
      state.transactions.unshift(action.payload);
    },
    clearInventoryError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch inventory
      .addCase(fetchInventory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInventory.fulfilled, (state, action) => {
        state.loading = false;
        state.inventory = action.payload;
      })
      .addCase(fetchInventory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create inventory item
      .addCase(createInventoryItemAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createInventoryItemAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.inventory.push(action.payload);
      })
      .addCase(createInventoryItemAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update inventory item
      .addCase(updateInventoryItemAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInventoryItemAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.inventory.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.inventory[index] = action.payload;
        }
      })
      .addCase(updateInventoryItemAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete inventory item
      .addCase(deleteInventoryItemAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteInventoryItemAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.inventory = state.inventory.filter(item => item.id !== action.payload);
      })
      .addCase(deleteInventoryItemAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch transactions
      .addCase(fetchTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload;
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create transaction
      .addCase(createTransactionAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTransactionAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions.unshift(action.payload);
      })
      .addCase(createTransactionAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setInventory,
  addInventoryItem,
  updateInventoryItem,
  removeInventoryItem,
  setTransactions,
  addTransaction,
  clearInventoryError,
} = keyInventorySlice.actions;
export default keyInventorySlice.reducer;
